// WhatsApp Management > Quick Replies — admin list of every quick reply (global + per instance).
// Chat users can also manage their own instance's replies from inside the chat ("/" picker);
// only here can a reply be made global or moved between instances.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Chip, MenuItem, TextField, Alert } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import moment from "moment";
import StyledDataGrid from "components/bento/StyledDataGrid";
import { getAllCustomerGroups } from "api/customerGroup";
import { listAllQuickReplies, saveQuickReply, deleteQuickReply } from "api/whatsapp";
import { QuickReplyEditDialog, QR_LANGS } from "./QuickReplies";

const WhatsAppQuickReplies = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [groups, setGroups] = useState([]);
  const [scope, setScope] = useState(""); // "" all | "global" | groupId
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [edit, setEdit] = useState(null); // { reply } — reply null = new

  const load = useCallback(async () => {
    setLoading(true);
    const result = await listAllQuickReplies(scope);
    setLoading(false);
    if (result.success) {
      setRows(result.data);
      setError("");
    } else {
      setError(result.message);
    }
  }, [scope]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    getAllCustomerGroups().then((result) => {
      if (result.success && Array.isArray(result.data)) setGroups(result.data.map((g) => ({ id: g.id, name: g.name })));
    });
  }, []);

  const groupName = useMemo(() => {
    const byId = new Map(groups.map((g) => [g.id, g.name]));
    return (id) => (id ? byId.get(id) || id : t("quickReplies.globalBadge"));
  }, [groups, t]);

  const columns = [
    { field: "shortcut", headerName: t("quickReplies.shortcut"), width: 150, valueGetter: (params) => `/${params.row.shortcut}` },
    { field: "title", headerName: t("quickReplies.titleLabel"), flex: 1, minWidth: 160 },
    {
      field: "groupId",
      headerName: t("quickReplies.scope"),
      width: 180,
      renderCell: (params) =>
        params.row.groupId ? groupName(params.row.groupId) : <Chip size="small" color="secondary" variant="outlined" label={t("quickReplies.globalBadge")} />,
    },
    {
      field: "text",
      headerName: t("quickReplies.languages"),
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {QR_LANGS.filter((l) => params.row.text[l]?.trim()).map((l) => (
            <Chip key={l} size="small" variant="outlined" label={l.toUpperCase()} />
          ))}
        </Box>
      ),
    },
    { field: "attachments", headerName: t("quickReplies.mediaCount"), width: 90, valueGetter: (params) => params.row.attachments.length },
    {
      field: "updatedAt",
      headerName: t("quickReplies.updated"),
      width: 150,
      valueFormatter: (params) => (params.value ? moment(params.value).format("DD/MM/YY HH:mm") : "-"),
    },
  ];

  const handleSave = async (data, files) => {
    const result = await saveQuickReply(null, edit?.reply?._id || null, data, files);
    if (result.success) {
      setEdit(null);
      load();
    }
    return result;
  };

  const handleDelete = async (reply) => {
    const result = await deleteQuickReply(null, reply._id);
    if (result.success) {
      setEdit(null);
      load();
    }
    return result;
  };

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
        <TextField select size="small" color="secondary" SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }} label={t("quickReplies.scope")} value={scope} onChange={(e) => setScope(e.target.value)} sx={{ minWidth: 220 }}>
          <MenuItem value="">{t("quickReplies.filterAll")}</MenuItem>
          <MenuItem value="global">{t("quickReplies.global")}</MenuItem>
          {groups.map((g) => (
            <MenuItem key={g.id} value={g.id}>
              {g.name}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="contained" color="secondary" startIcon={<AddIcon />} onClick={() => setEdit({ reply: null })} sx={{ ml: "auto" }}>
          {t("quickReplies.new")}
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box sx={{ height: 600 }}>
        <StyledDataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row._id}
          loading={loading}
          onRowClick={(params) => setEdit({ reply: params.row })}
          containerSx={{ height: "100%" }}
          autoHeight={false}
        />
      </Box>
      <QuickReplyEditDialog
        open={!!edit}
        reply={edit?.reply || null}
        defaultGroupId={scope && scope !== "global" ? scope : ""}
        groups={groups}
        onClose={() => setEdit(null)}
        onSave={handleSave}
        onDelete={handleDelete}
        t={t}
      />
    </Box>
  );
};

export default WhatsAppQuickReplies;
